import React, { ReactNode, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { LoadingOutlined } from '@ant-design/icons';
import { AutoComplete as AutoCompleteAnt } from 'antd';
import type { SizeType } from 'antd/lib/config-provider/SizeContext';

export type AutoCompleteProps<T> = {
  popupMatchSelectWidth?: boolean;
  showError?: boolean;
  initialSearchActive?: boolean;
  disabled?: boolean;
  allowClear?: boolean;
  value?: T;
  onSearch: (searchText: string) => Promise<T[] | undefined>;
  isPreSelectItemInvalid?: (data: T | undefined) => boolean;
  onChange?: (data: T | undefined) => void;
  onBlur?: () => Promise<void>;
  renderOption?: (item: T) => ReactNode | HTMLElement | string;
  labelSearchExtractor?: (item: T) => string;
  labelExtractor: (item: T) => string;
  keyExtractor: (item: T) => string;
  style?: React.CSSProperties;
  className?: string;
  size?: SizeType;
  placeholder?: string;
  notFoundContent?: ReactNode;
};

type OptionType<T> = {
  value: string;
  label: ReactNode | HTMLElement | string;
  data: T;
};

const AutoComplete = <T,>({
                            showError,
                            initialSearchActive,
                            disabled,
                            value,
                            onSearch,
                            isPreSelectItemInvalid,
                            onChange,
                            onBlur,
                            renderOption,
                            labelSearchExtractor,
                            labelExtractor,
                            keyExtractor,
                            className,
                            style = {},
                            notFoundContent,
                            popupMatchSelectWidth = false,
                            ...props
                          }: AutoCompleteProps<T>) => {
  const initialValue = !!value ? labelExtractor(value) : '';
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState(initialValue);

  const [searchObj, setSearchObj] = useState(value);
  const [result, setResult] = useState<T[]>([]);
  //const [open, setOpen] = useState(false);

  useEffect(() => {
    if (initialSearchActive) {
      debouncedHandleOnChange();
    }
  }, []);

  /**
   * Updates date when value changes
   */
  useEffect(() => {
    if (initialValue !== searchText) {
      setSearchText(initialValue || '');
      setSearchObj(value);
    }
  }, [value]);

  /**
   * Handles clear button click
   */
  const onSelectHandler = (data: string, option: OptionType<T>) => {
    const label = labelExtractor(option.data);
    setSearchText(label);

    if (isPreSelectItemInvalid && isPreSelectItemInvalid(option.data)) {
      //setOpen(true);
      return;
    }

    setSearchObj(option.data);
    onChange && onChange(option.data);
    //setOpen(false);
  };

  /**
   * Handles clear button click
   */
  const onClearHandler = () => {
    setSearchText('');
    setSearchObj(undefined);
    onChange && onChange(undefined);
  };

  /**
   * Handles search text input change
   */
  const onChangeHandler = (text: string) => {
    !loading && setLoading(true);

    setSearchText(text);
    setSearchObj(undefined);
    debouncedHandleOnChange();
    //setOpen(true);
  };

  /**
   * Sends request to the server to search data
   */
  const debouncedHandleOnChange = useDebouncedCallback(async () => {
    const res =
      (labelSearchExtractor && searchObj
        ? await onSearch(labelSearchExtractor(searchObj))
        : await onSearch(searchText)) ?? [];

    setResult(res);
    setLoading(false);
  }, 500);

  /**
   * Produces options for AutoComplete
   */
  const options: OptionType<T>[] = useMemo(() => {
    return result.map(item => ({
      value: keyExtractor(item),
      label: renderOption ? renderOption(item) : labelExtractor(item),
      data: item,
    }));
  }, [result]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        position: 'relative',
        ...style
      }}>
      <AutoCompleteAnt
        //open={open}

        status={showError ? 'error' : undefined}
        disabled={disabled}
        autoFocus={false}
        value={searchText}
        options={options}
        onSelect={onSelectHandler}
        onChange={onChangeHandler}
        onClear={onClearHandler}
        style={{ minWidth: 200, ...style }}
        className={className}
        onBlur={onBlur}
        notFoundContent={
          !!searchText && !result?.length && !loading ? notFoundContent : undefined
        }
        popupMatchSelectWidth={!!popupMatchSelectWidth}
        {...props}
      />

      {loading && (
        <div style={{ position: 'absolute', right: 12 }}>
          <LoadingOutlined />
        </div>
      )}
    </div>
  );
};

export default AutoComplete;
